import * as React from 'react';
import ReactHotToast from 'react-hot-toast';
import { useLocation, useRouteMatch } from 'react-router-dom';

import { trackEvent } from '@/analytics/trackEvent';
import { useAppFeedMatch } from '@/components/AppHeader/hooks/useCurrentFeedOption';
import {
  type AskNeedlCitationType,
  type AskNeedlSentenceType,
  useSnipeNote,
} from '@/components/ChatBot/hooks';
import { type FormattedData, formatForShare } from '@/components/ChatBot/utils';
import type {
  AskNeedlReportType,
  SearchWithinType,
} from '@/features/ChatBotV2/context';
import {
  NEEDL_UNIVERSE_ID,
  useAskNeedlDispatchContext,
} from '@/features/ChatBotV2/context';
import { useAskNeedl } from '@/features/ChatBotV2/hooks/useAskNeedl';
import { ASK_NEEDL_ACTIONS } from '@/features/ChatBotV2/hooks/useAskNeedlState';
import {
  type MetadataType,
  type SessionState,
  isBotMessageState,
  isUserMessageState,
} from '@/features/ChatBotV2/types';
import { getMixpanelLabelForReportType } from '@/features/NeedlReport/common';
import {
  useReportPage,
  useSelectReportFileIds,
} from '@/features/NeedlReport/hooks/useReport';
import { useSyncReportChat } from '@/features/NeedlReport/hooks/useSyncReportChat';
import { useConfigUI } from '@/hooks/query/useFeatureConfig';
import { useBoardFromURL } from '@/hooks/useBoardFromURL';
import { useCurrentDocument } from '@/hooks/useCurrentDocument';
import { useGetSearchType } from '@/hooks/useGetSearchType';
import { ASKNEEDL_FULLSCREEN_ROUTE } from '@/nav-routes';
import { useNavigate } from '@/router';
import { CommonErrorToast } from '@/utils/toasts';

import { useCreateUpdateSession } from './useCreateUpdateSession';
import { useFetchSessionById } from './useFetchSessionById';
import { getRequestFieldsFromMetadata } from './useGetSessionMetadata';
import { useHandleFilesDrop } from './useHandleFilesDrop';

export const useChatPanelLogic = ({
  currentFeedOrDocumentId,
  searchWithin,
  sessionState,
  pro,
  report,
  lastActiveSessionId,
}: ChatPanelLogicType) => {
  const abortController = React.useRef<AbortController | null>(null);
  const dispatch = useAskNeedlDispatchContext();
  const upsertSessionMutation = useCreateUpdateSession();
  const { data: sessionData, isLoading: isSessionLoading } =
    useFetchSessionById(lastActiveSessionId as string);
  const { isLoading: isFeatureConfigLoading, data: featureConfig } =
    useConfigUI();
  const isAskNeedlPageRoute = !!useRouteMatch(ASKNEEDL_FULLSCREEN_ROUTE);
  const { handleFilesDrop } = useHandleFilesDrop(
    sessionState.attachmentsFeedId,
    sessionState.attachments
  );

  const {
    params: { source },
  } = useRouteMatch<{
    source: string;
  }>(['/:category/:source/:subCategory']) || {
    params: {},
  };

  const isAppFeed = useAppFeedMatch();
  const searchType = useGetSearchType();
  const navigate = useNavigate();
  const location = useLocation();

  const { reportPageMatch } = useReportPage();
  const { selectedIds } = useSelectReportFileIds();

  const lastUserMessage = React.useMemo(() => {
    if (sessionState.messages.length === 0) {
      return;
    }

    const userMessages = sessionState.messages.filter(isUserMessageState);

    if (userMessages.length === 0) {
      return;
    }

    const lastUserMessage = userMessages[userMessages.length - 1];
    return lastUserMessage;
  }, [sessionState.messages]);

  const {
    streamingAnswer,
    intermediateSteps,
    data: askNeedlData,
    askNeedl,
    clearEventData,
    isLoading,
    isStreaming,
    isError,
  } = useAskNeedl();

  const snipNoteMutation = useSnipeNote();
  const { board_id, channel_id } = useBoardFromURL() ?? {};
  const currentDocument = useCurrentDocument();

  const hasTrackedFirstChunkRef = React.useRef(false);
  const questionStartTimeRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    // close the sse connection when the current feed or document id changes
    if (abortController.current) {
      abortController.current.abort();
    }

    questionStartTimeRef.current = null;

    clearEventData();

    return () => {
      if (abortController.current) {
        abortController.current.abort();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFeedOrDocumentId, searchWithin, lastActiveSessionId]);

  React.useEffect(() => {
    if (intermediateSteps && intermediateSteps.length > 0) {
      dispatch({
        type: ASK_NEEDL_ACTIONS.SET_SESSION_INTERMEDIATE_STEPS,
        payload: {
          botMessageId: lastUserMessage?.botMessageId ?? '',
          prompt: lastUserMessage?.prompt ?? '',
          intermediateSteps,
        },
      });
    }
  }, [dispatch, intermediateSteps, lastUserMessage]);

  React.useEffect(() => {
    if (streamingAnswer && !hasTrackedFirstChunkRef.current) {
      if (report.enabled) {
        trackEvent(
          `${getMixpanelLabelForReportType(
            report.info?.id
          )}_chat_response_started`
        );
      }

      hasTrackedFirstChunkRef.current = true;
    }
  }, [streamingAnswer, report.enabled, report.info?.id]);

  React.useEffect(() => {
    if (streamingAnswer) {
      dispatch({
        type: ASK_NEEDL_ACTIONS.SET_SESSION_WHILE_STREAMING,
        payload: {
          botMessageId: lastUserMessage?.botMessageId ?? '',
          prompt: lastUserMessage?.prompt ?? '',
          streamingAnswer,
        },
      });
    }
  }, [dispatch, lastUserMessage, streamingAnswer]);

  React.useEffect(() => {
    if (!isStreaming) {
      hasTrackedFirstChunkRef.current = false;
    }
  }, [isStreaming]);

  React.useEffect(() => {
    if (askNeedlData) {
      if (report.enabled) {
        const maxCitationId = Math.max(
          ...(askNeedlData.generated_answer?.sentences
            .flatMap((sentence: AskNeedlSentenceType) => sentence.citations)
            .map((citation: AskNeedlCitationType) => citation.id) || [0])
        );

        const responseTime = questionStartTimeRef.current
          ? Date.now() - questionStartTimeRef.current
          : 0;

        trackEvent(
          `${getMixpanelLabelForReportType(
            report.info?.id
          )}_chat_response_completed`,
          {
            response_length: askNeedlData.generated_answer?.answer.length,
            citation_count: maxCitationId,
            response_time: responseTime / 1000,
          }
        );

        questionStartTimeRef.current = null;
      }

      dispatch({
        type: ASK_NEEDL_ACTIONS.SET_SESSION_AFTER_COMPLETE,
        payload: {
          botMessageId: lastUserMessage?.botMessageId ?? '',
          prompt: lastUserMessage?.prompt ?? '',
          askNeedlData,
          onComplete: (session) => {
            if (
              isFeatureConfigLoading ||
              !featureConfig?.ask_needl_history ||
              session.metadata?.type === 'report'
            ) {
              return;
            }
            upsertSessionMutation.mutate(
              {
                currentFeedOrDocumentId,
                searchWithin,
                sessionState: session,
              },
              {
                onSuccess: () => {
                  if (isAskNeedlPageRoute) {
                    dispatch({
                      type: ASK_NEEDL_ACTIONS.SET_LAST_ACTIVE_SESSION,
                      payload: {
                        lastActiveSessionId: session.sessionId as string,
                      },
                    });
                  }
                },
                onError: () => {
                  ReactHotToast.error('Session sync failed');
                },
              }
            );
          },
        },
      });

      clearEventData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [askNeedlData, clearEventData, dispatch, lastUserMessage, report.enabled]);

  React.useEffect(() => {
    if (isSessionLoading) {
      return;
    }

    if (lastActiveSessionId && sessionData && isAskNeedlPageRoute) {
      dispatch({
        type: ASK_NEEDL_ACTIONS.SET_SESSION,
        payload: {
          id: sessionData.state.currentFeedOrDocumentId,
          searchWithin: sessionData.state.searchWithin as SearchWithinType,
          lastActiveSessionId,
          newSession: {
            messages: sessionData.state.messages,
            attachments: sessionData.state?.attachments || [],
            attachmentsFeedId: sessionData.state?.attachmentsFeedId,
            sessionId: lastActiveSessionId,
            pro: sessionData.state.settings.pro,
            metadata:
              sessionData.state.metadata ||
              ({
                type: sessionData.state.currentFeedOrDocumentId,
                board_id: sessionData.state.currentFeedOrDocumentId,
              } as MetadataType),
          },
        },
      });
      dispatch({
        type: ASK_NEEDL_ACTIONS.CHANGE_CURRENT_SESSION,
        payload: {
          searchWithin: sessionData.state.searchWithin,
          currentFeedOrDocumentId: sessionData.state.currentFeedOrDocumentId,
        },
      });
    }
  }, [
    dispatch,
    lastActiveSessionId,
    sessionData,
    isSessionLoading,
    isAskNeedlPageRoute,
  ]);

  useSyncReportChat({
    enabled: report.enabled,
    currentFeedOrDocumentId,
    sessionState,
  });

  const saveToNoteHandler = () => {
    if (!sessionState?.messages.length || sessionState?.messages.length < 2) {
      return;
    }

    const prompt = sessionState?.messages[0].prompt;
    const snipData = sessionState?.messages
      .filter(({ type }) => type === 'bot')
      .map((data) => {
        if (isBotMessageState(data)) {
          return formatForShare(data.prompt, data.data, 'object');
        }
        return null;
      })
      .filter(Boolean);

    if (!snipData) {
      CommonErrorToast();
      return;
    }

    snipNoteMutation.mutate(
      {
        conversation: snipData as FormattedData[],
        promt: prompt,
      },
      {
        onSuccess: () => {
          trackEvent('ask_needl_note_created', {
            question: prompt,
          });
          ReactHotToast.success('Saved to Needl Note!');
        },
        onError: () => {
          CommonErrorToast();
        },
      }
    );
  };

  const handleAskQuestion = React.useCallback(
    async (prompt: string) => {
      if (prompt.length > 0) {
        clearEventData();
        dispatch({
          type: ASK_NEEDL_ACTIONS.SET_SESSION_ON_QUESTION,
          payload: prompt,
        });

        if (report.enabled) {
          trackEvent(
            `${getMixpanelLabelForReportType(
              report.info?.id
            )}_chat_question_submitted`,
            {
              question: prompt,
              question_length: prompt.length,
            }
          );
        } else {
          trackEvent('ask_needl_ques_asked', {
            question: prompt,
            'Pro Enabled': String(pro),
            is_old_session: !!lastActiveSessionId,
            is_doc_query: !!sessionState.attachmentsFeedId,
            ...(!!sessionState.attachmentsFeedId && {
              is_first_doc_query: sessionState.messages.length === 0,
              file_count: sessionState.attachments?.length || 0,
            }),
          });
        }

        if (abortController.current) {
          abortController.current.abort();
        }
        abortController.current = new AbortController();

        questionStartTimeRef.current = Date.now();

        await askNeedl({
          prompt,
          ...(isAppFeed && searchWithin === 'feed' ? { source } : {}),
          session_id: sessionState?.sessionId,
          ...(searchWithin === 'document' && {
            document_id: currentDocument?.deeplink,
            channel_id,
            ...(searchType && {
              board_id: searchType,
            }),
          }),
          ...(searchWithin === 'feed' && {
            board_id,
            channel_id,
            ...(reportPageMatch && { document_id: selectedIds }),
          }),
          ...(currentFeedOrDocumentId === NEEDL_UNIVERSE_ID && {
            board_id: NEEDL_UNIVERSE_ID,
          }),
          ...(sessionState.metadata &&
            !reportPageMatch &&
            getRequestFieldsFromMetadata(sessionState.metadata)),
          ...(sessionState?.attachmentsFeedId && {
            board_id: sessionState.attachmentsFeedId,
          }),
          pro,
        });
      }
    },
    [
      askNeedl,
      board_id,
      channel_id,
      clearEventData,
      currentDocument?.deeplink,
      currentFeedOrDocumentId,
      dispatch,
      isAppFeed,
      lastActiveSessionId,
      pro,
      reportPageMatch,
      searchType,
      searchWithin,
      selectedIds,
      sessionState.attachmentsFeedId,
      sessionState.sessionId,
      source,
      report.enabled,
      report.info?.id,
      sessionState.messages.length,
      sessionState.attachments,
      sessionState.metadata,
    ]
  );

  const handleClear = () => {
    if (abortController.current) {
      abortController.current.abort();
    }

    questionStartTimeRef.current = null;

    clearEventData();
    dispatch({
      type: ASK_NEEDL_ACTIONS.SET_SESSION_ON_CLEAR,
      payload: {
        searchWithin,
      },
    });

    const queryParams = new URLSearchParams(location.search);
    queryParams.delete('sessionId');
    navigate.replace(
      {
        pathname: location.pathname,
        search: queryParams.toString(),
      },
      null,
      {
        removeOldState: false,
      }
    );

    trackEvent('ask_needl_new_topic');
  };

  const stopGenerating = React.useCallback(() => {
    if (abortController.current) {
      abortController.current.abort();
    }

    questionStartTimeRef.current = null;

    dispatch({
      type: ASK_NEEDL_ACTIONS.STOP_SESSION_WHILE_STREAMING,
      payload: {
        botMessageId: lastUserMessage?.botMessageId ?? '',
        prompt: lastUserMessage?.prompt ?? '',
      },
    });

    clearEventData();
  }, [dispatch, lastUserMessage, clearEventData]);

  return {
    handleAskQuestion,
    handleClear,
    handleFilesDrop,
    hasMessages: sessionState?.messages.length > 0,
    saveToNoteHandler,
    isLoading,
    isStreaming,
    isError,
    snipNoteMutation,
    stopGenerating,
  };
};

type ChatPanelLogicType = {
  currentFeedOrDocumentId: string;
  searchWithin: SearchWithinType;
  sessionState: SessionState;
  pro: boolean;
  report: AskNeedlReportType;
  lastActiveSessionId: string | null;
};
